if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        /*
          Defensive: <product-form> may wrap markup WITHOUT a <form>
          tag — e.g. multi-variant catalog cards where the button is
          a button[type=button] that opens the eisNova overlay rather
          than submits a form. In that case the legacy Dawn submit flow
          doesn't apply and we just bail out of constructor.

          Also: jQuery jcarousellite plugin clones DOM nodes for its
          infinite-loop carousel, which triggers connectedCallback /
          constructor on disconnected fragments. Without these guards,
          a single TypeError here crashes ALL <product-form> instances
          on the page — which kills click handlers everywhere.
        */
        this.form = this.querySelector('form');
        if (!this.form) {
          /* No form inside — this is a "trigger-only" wrapper (overlay opener
             or other custom add-to-cart path). Nothing to wire up. */
          return;
        }

        const idInput = this.form.querySelector('[name=id]');
        if (idInput) idInput.disabled = false;

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      /*
        Validates cart limits BEFORE submitting to /cart/add.
        Reads product tags from data-product-tags attribute on the
        <product-form> element (rendered by Liquid).

        Two rules (matching overlay + cart-drawer behavior):
          - DISCOVERY SETS tag: hard cap 1 per order
          - Generic 4-piece cap: same-variant total in cart cannot exceed 4

        Returns user-facing error string when invalid, null when OK.
      */
      /*
        Unified per-product cap validator. cap=1 for DS-tagged products,
        cap=4 otherwise. Customers may hold multiple DIFFERENT discovery
        sets (1 each); only stockpiling the SAME one is blocked.

        product_id read from data-product-id attribute on <product-form>
        (rendered by Liquid). Falls back to variant-based grouping if the
        attribute is missing — degrades gracefully on legacy templates.
      */
      async _validateLimits(formData) {
        const tagsAttr = (this.getAttribute('data-product-tags') || '').toLowerCase();
        const tags = tagsAttr.split(',').map(t => t.trim()).filter(Boolean);
        const isDiscoverySet = tags.includes('discovery sets');
        const cap = isDiscoverySet ? 1 : 4;
        const productLabel = isDiscoverySet ? 'Discovery Set' : 'this item';

        const productId = Number(this.getAttribute('data-product-id') || 0);
        const variantId = Number(formData.get('id'));
        const requestedQty = parseInt(formData.get('quantity') || '1', 10);

        /* Fast fail: requested qty alone over the cap */
        if (requestedQty > cap) {
          return `Sorry, you cannot add more than ${cap} of ${productLabel} per order.`;
        }

        /* Fetch cart to count existing units of this product */
        let cart;
        try {
          cart = await fetch('/cart.js').then(r => r.json());
        } catch (err) {
          return null;
        }

        /*
          Group cart lines by product_id (preferred) or variant_id (fallback).
          Same product across multiple lines (different selling-plan, properties)
          all count toward the cap.
        */
        const currentQty = (cart.items || [])
          .filter(item => productId
            ? Number(item.product_id) === productId
            : Number(item.variant_id) === variantId)
          .reduce((sum, item) => sum + (item.quantity || 0), 0);

        if (currentQty + requestedQty > cap) {
          const remaining = Math.max(0, cap - currentQty);
          if (remaining === 0) {
            return `Sorry, you cannot add more than ${cap} of ${productLabel} per order.`;
          }
          return `Sorry, you can only add ${remaining} more of ${productLabel} (limit: ${cap} per order).`;
        }

        return null;
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const formData = new FormData(this.form);

        /* Pre-flight limit check — show toast + abort if violated.
           Skipped silently if EisNovaToast isn't available (e.g., on
           pages that don't load eisNova-overlay.js). */
        const limitError = await this._validateLimits(formData);
        if (limitError) {
          if (window.EisNovaToast) {
            window.EisNovaToast.show(limitError);
          } else {
            this.handleErrorMessage(limitError);
          }
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id'), cartData: response });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}